import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import googleLogo from "../img/google.png";
import { auth, db } from "./firebase.js";
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged
} from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Helper function to check if email exists in Firestore
  const checkEmailInFirestore = async (userEmail) => {
    const q = query(collection(db, "users"), where("email", "==", userEmail));
    const snap = await getDocs(q);
    return !snap.empty;
  };

  // Check if user is already authenticated (persistent login)
  // Credentials are stored securely in browser's key store
  // User will be automatically logged in if they haven't manually logged out
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is already logged in (credentials restored from secure storage)
        // Check if they exist in Firestore
        try {
          const exists = await checkEmailInFirestore(user.email);
          if (exists) {
            // User is authenticated and exists in Firestore
            // Redirect to dashboard immediately (credentials saved in key store)
            console.log("✅ User credentials found in secure storage, redirecting to dashboard");
            console.log("✅ User will stay logged in until manual logout");
            // Use replace: true to prevent back button from going back to login
            navigate("/dashboard", { replace: true });
          } else {
            // User authenticated but not in Firestore, sign them out
            console.log("⚠️ User not in Firestore, signing out");
            await auth.signOut();
          }
        } catch (error) {
          console.error("Error checking user in Firestore:", error);
        }
      } else {
        // No user found - credentials not in secure storage
        // User needs to login
        console.log("ℹ️ No saved credentials found - user needs to login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Check for redirect result on component mount
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          setLoading(true);
          const user = result.user;
          const exists = await checkEmailInFirestore(user.email);

          if (!exists) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: "Your Google Account is not allowed to login!",
            });
            setLoading(false);
            return;
          }

          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Redirect result error:", error);
        setLoading(false);
      }
    };

    checkRedirectResult();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    // Prevent multiple simultaneous clicks
    if (loading) return;
    
    setLoading(true);
    const provider = new GoogleAuthProvider();
    
    // Add additional scopes if needed
    provider.addScope('profile');
    provider.addScope('email');
    
    // Set custom parameters
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      // Try popup first (better UX)
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const exists = await checkEmailInFirestore(user.email);

      if (!exists) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Your Google Account is not allowed to login!",
        });
        setLoading(false);
        await auth.signOut(); // Sign out if not authorized
        return;
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("Google sign-in error:", error);
      
      // Handle specific error types
      if (error.code === 'auth/popup-blocked') {
        // Popup was blocked, fallback to redirect
        Swal.fire({
          icon: 'info',
          title: 'Popup Blocked',
          text: 'Please allow popups for this site, or we will redirect you to Google sign-in.',
          confirmButtonText: 'Continue with Redirect',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            signInWithRedirect(auth, provider);
            // Note: setLoading will be handled by redirect result check
          } else {
            setLoading(false);
          }
        });
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "Sign-in cancelled",
          timer: 2000
        });
        setLoading(false);
      } else if (error.code === 'auth/network-request-failed') {
        // Network error
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Network error. Please check your connection.",
        });
        setLoading(false);
      } else if (error.code === 'auth/unauthorized-domain') {
        // Domain not authorized in Firebase Console
        Swal.fire({
          icon: 'error',
          title: 'Configuration Error',
          text: 'This domain is not authorized. Please contact the administrator.',
        });
        setLoading(false);
      } else {
        // Generic error - try redirect as fallback
        Swal.fire({
          icon: 'warning',
          title: 'Sign-in Issue',
          text: 'Popup sign-in failed. Redirecting to Google sign-in...',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          signInWithRedirect(auth, provider);
        });
      }
    }
  };

  return (
    <div
      className="login-container-responsive"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        position: "relative",
      }}
    >
      {/* Subtle background pattern */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <div
        className="p-5 p-md-5 login-card-responsive"
        style={{
          width: "100%",
          maxWidth: "420px",
          borderRadius: "16px",
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          color: "#111827",
          animation: "fadeIn 0.6s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Logo/Brand area */}
        <div className="text-center mb-5">
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
            }}
          >
            <span style={{ color: "white", fontSize: "28px", fontWeight: 700 }}>J</span>
          </div>
          <h2 className="fw-semibold mb-2 login-title-responsive" style={{ color: "#111827", fontSize: "1.75rem", letterSpacing: "-0.02em" }}>
            Welcome Back
          </h2>
          <p style={{ color: "#6b7280", fontSize: "0.9375rem", margin: 0 }}>
            Sign in to continue to your workspace
          </p>
        </div>

        <div className="text-center">
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="btn w-100 d-flex align-items-center justify-content-center login-button-responsive"
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              padding: "14px 20px",
              borderRadius: "10px",
              fontSize: "0.9375rem",
              color: "#374151",
              fontWeight: 500,
              transition: "all 0.2s ease",
              position: "relative",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.08)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {!loading ? (
              <>
                <img
                  src={googleLogo}
                  alt="Google"
                  className="google-logo-responsive"
                  style={{ width: "20px", height: "20px", marginRight: "12px" }}
                />
                <span className="login-button-text">Sign in with Google</span>
              </>
            ) : (
              <>
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ width: "18px", height: "18px", marginRight: "12px", borderWidth: "2px", color: "#2563eb" }}
                ></div>
                <span>Signing in...</span>
              </>
            )}
          </button>

          {/* Footer text */}
          <p style={{ color: "#9ca3af", fontSize: "0.8125rem", marginTop: "24px", marginBottom: 0 }}>
            By signing in, you agree to our terms of service
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </div>
  );
}
